/* eslint-disable @typescript-eslint/no-explicit-any */
import { Link, Outlet, useLocation } from "react-router-dom";
import Navbar from "./components/Navbar/Navbar";
import { useAppDispatch, useAppSelector } from "@/src/app/hooks";
import { useEffect, useState } from "react";
import { setLastPath } from "@/src/features/userSlice";
import { Affix } from "@mantine/core";
import api from "@/src/utils";
import apiRoutes from "@/src/apiRoutes";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Card } from "@/src/components/shad/card";
import { textVariants } from "@/src/components/Text/Text";
import _ from "lodash";
import { DrawerSheet } from "@/src/components/DrawerSheet/DrawerSheet";
import { cn } from "@/lib/utils";
import Footer from "@/src/components/Footer";
import transferIcon from "@assets/icons/transfer.svg";
import { ArrowRight, ChevronRight } from "lucide-react";
import { ActiveTradeData, Survey } from "./types";
import { useMediaQuery } from "usehooks-ts";
import { toast } from "sonner";
import { Modal } from "@/src/components/Modal/Modal";
import Surveys from "@/src/pages/App/surveys";
import { useTranslation } from "react-i18next";
import { TOptions } from "i18next";
import * as Sentry from "@sentry/react";

const Dashboard = () => {
  const { t } = useTranslation();
  const trans = (key: string, options?: TOptions) =>
    t(`app.pendingTrade.${key}`, options);
  const { user } = useAppSelector((state) => state.user);
  const [openActiveTrade, setOpenActiveTrade] = useState(false);

  const dispatch = useAppDispatch();
  const location = useLocation();
  const isMobile = useMediaQuery("(max-width: 768px)");

  const queryClient = useQueryClient();

  Sentry.setUser({ username: user?.username });

  const getActiveTrades = async () => {
    const res = await api.post(apiRoutes.checkEnablers, {
      type: "active_trades",
    });
    return res.data;
  };

  const { data } = useQuery<ActiveTradeData>({
    queryKey: [`check_enablers_active_trades`],
    queryFn: getActiveTrades,
  });
  const getSurveyStatus = async () => {
    const res = await api.post(apiRoutes.checkEnablers, {
      type: "survey",
    });
    return res.data;
  };

  const { data: surveyActive } = useQuery<Survey>({
    queryKey: [`check_enablers_survey`],
    queryFn: getSurveyStatus,
  });

  const cancelB2C = async (channel: string) => {
    const res = await api.post(apiRoutes.cancel_trade, {
      channel: channel,
      type: "b2c",
    });
    return res.data;
  };

  const { mutate, isLoading } = useMutation({
    mutationFn: cancelB2C,
    onSuccess: (data) => {
      if (data?.success) {
        setOpenActiveTrade(false);
        toast.success("Trade cancelled successfully");
        queryClient.invalidateQueries([`check_enablers_active_trades`]);
      }
    },
  });

  useEffect(() => {
    dispatch(setLastPath(location.pathname));
  }, [dispatch, location]);

  if (!user?.token) return null;

  return (
    <>
      <div className="overflow-x-hiddden dark:bg-grey-900">
        <Navbar />
        <div className="min-h-[94vh] mt-16 bg-[#F6F8FF]  dark:bg-grey-900">
          {/* <EmergencyNotifications /> */}
          <Outlet />
        </div>
        {(!window.location.href.includes("/app/trade/ad/") || !isMobile) && (
          <Footer />
        )}
        {data?.success &&
          !_.isEmpty(data?.info) &&
          location.pathname.includes("app/home") && (
            <Affix position={{ bottom: 20 }} w={"100%"} zIndex={20}>
              <div className=" w-full flex items-center justify-center  ">
                <div
                  onClick={() => setOpenActiveTrade(true)}
                  className="px-4 py-2 border border-blue-700 rounded-lg bg-[#F6F8FF] dark:bg-grey-500 flex items-center md:space-x-10 justify-between cursor-pointer"
                >
                  <img src={transferIcon} alt="item" className="h-10 w-10" />
                  <div className="grid">
                    <span className={textVariants({})}>
                      {trans("card.title")}
                    </span>
                    <span
                      className={textVariants({ size: "xs", variant: "desc" })}
                    >
                      {trans("card.description")}
                    </span>
                  </div>
                  <div className="flex">
                    <span className="bg-grey-50 rounded-full p-2">
                      <ArrowRight className="text-blue-600 h-4 w-4" />
                    </span>
                  </div>
                </div>
              </div>
            </Affix>
          )}
        <DrawerSheet
          title={trans("drawer.title")}
          size="sm"
          open={openActiveTrade}
          setOpen={setOpenActiveTrade}
          setter="state"
        >
          <div className="grid gap-3">
            {data?.info?.map((item, idx) =>
              item?.trade_type === "p2p" ? (
                <Card className=" bg-input-field p-2  cursor-pointer" key={idx}>
                  <Link
                    reloadDocument
                    onClick={() => setOpenActiveTrade(false)}
                    to={`${window.location.origin}/app/trade/ad/${item?.channel}`}
                    className="flex justify-between items-center"
                  >
                    <div className="grid">
                      <div className="flex items-center gap-1">
                        <img
                          className="h-6 w-6"
                          src={item?.commodity__coin__image}
                          alt={item?.commodity__coin__ticker}
                        />
                        <div className="flex flex-col gap-0">
                          <span
                            className={textVariants({ case: "capitalize" })}
                          >
                            {item?.trans_type}{" "}
                            <span
                              className={textVariants({
                                font: "semibold",
                                case: "uppercase",
                                size: "sm",
                              })}
                            >
                              {item?.commodity__coin__ticker}
                            </span>
                          </span>
                          <span className="-mt-2">
                            <span
                              className={textVariants({
                                size: "xs",
                                variant: "desc",
                              })}
                            >
                              {trans("drawer.to")}:{" "}
                            </span>
                            <span className={textVariants({ size: "xs" })}>
                              {item?.partner}
                            </span>
                          </span>
                        </div>
                      </div>
                      <span className="text-xs">
                        <span>{trans("drawer.tradingAmount")}: </span>
                        <span>
                          {item?.amount_local} {""}
                          {item?.commodity__currency}
                        </span>
                      </span>
                    </div>
                    <div className="h-full">
                      <div className="flex flex-col justify-between h-full items-end gap-4">
                        <span>{item?.commodity__unit_price}</span>
                        <ChevronRight className="h-5 w-5 text-indigo-400" />
                      </div>
                    </div>
                  </Link>
                </Card>
              ) : (
                <Card
                  key={idx}
                  className="p-2 flex items-center justify-between gap-3 "
                >
                  <div className="grid gap-1">
                    <span className={cn(textVariants(), "capitalize")}>
                      {trans("drawer.buyTrade.title")}
                    </span>
                    <span className={textVariants({ size: "sm" })}>
                      {/* You have an active {item?.trans_type} trade. Kindly
                      complete the trade{" "} */}
                      {trans("drawer.buyTrade.desc", {
                        type: item?.trans_type,
                      })}
                      {item?.trans_type === "buy" &&
                        "by completing payment your payment transaction."}
                    </span>
                  </div>
                  <div className="grid gap-5">
                    <Link
                      reloadDocument
                      onClick={() => setOpenActiveTrade(false)}
                      to={"/app/wallet/history?tab=direct"}
                      className={cn(
                        textVariants({
                          variant: "link",
                          size: "xs",
                          font: "semibold",
                        }),
                        "underline cursor-pointer text-center"
                      )}
                    >
                      {trans("drawer.buyTrade.view")}
                    </Link>
                    {item?.trans_type === "buy" && (
                      <span
                        className={cn(
                          textVariants({
                            variant: "link",
                            size: "xs",
                            font: "semibold",
                          }),
                          "underline cursor-pointer text-red-500 whitespace-nowrap",
                          isLoading && "cursor-wait"
                        )}
                        onClick={() => mutate(item?.channel)}
                      >
                        {trans("drawer.buyTrade.cancelTrade")}
                      </span>
                    )}
                  </div>
                </Card>
              )
            )}
          </div>
        </DrawerSheet>
      </div>
      <Modal
        open={surveyActive?.info.active ?? false}
        setOpen={console.log}
        closeIcon={false}
      >
        <Surveys survey={surveyActive?.info} />
      </Modal>
    </>
  );
};

export default Dashboard;
